'use client';

import React from 'react';

import * as SubframeUtils from '../utils';

type TabsButtonBorderSize = 'sm' | 'md';

interface TabsButtonBorderItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  badge?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}

const Item = React.forwardRef<HTMLButtonElement, TabsButtonBorderItemProps>(function Item(
  { value, badge, active = false, disabled = false, className, type = 'button', ...otherProps },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      role="tab"
      aria-selected={active}
      aria-disabled={disabled}
      disabled={disabled}
      className={SubframeUtils.twClassNames(
        'focus-visible:ring-brand-200 flex items-center justify-center gap-2 rounded-[calc(var(--radius-md)-2px)] px-3 whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none',
        {
          'text-default-font bg-white shadow-sm': active,
          'text-subtext-color hover:bg-neutral-100': !active,
          'cursor-default text-neutral-400 hover:bg-transparent': disabled,
        },
        className,
      )}
      {...otherProps}
    >
      <span className="text-body font-semibold">{otherProps.children}</span>
      {badge !== undefined && badge !== null ? (
        <span className="inline-flex items-center rounded-full border border-solid border-neutral-200 bg-neutral-50 px-2 py-0.5">
          <span className="text-caption text-default-font font-medium">{badge}</span>
        </span>
      ) : null}
    </button>
  );
});

interface TabsButtonBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  size?: TabsButtonBorderSize;
  fullWidth?: boolean;
  className?: string;
}

const TabsButtonBorderRoot = React.forwardRef<HTMLDivElement, TabsButtonBorderProps>(
  function TabsButtonBorderRoot(
    { children, value, onValueChange, size = 'sm', fullWidth = true, className, ...otherProps },
    ref,
  ) {
    const isItemElement = (
      child: React.ReactNode,
    ): child is React.ReactElement<TabsButtonBorderItemProps> => React.isValidElement(child);

    return (
      <div
        ref={ref}
        role="tablist"
        className={SubframeUtils.twClassNames(
          'flex w-full items-center gap-1 rounded-[var(--radius-md)] border border-solid border-neutral-200 bg-neutral-50 p-1',
          {
            'h-11': size === 'sm',
            'h-12': size === 'md',
          },
          className,
        )}
        {...otherProps}
      >
        {children
          ? React.Children.map(children, (child) => {
              if (!isItemElement(child)) return child;

              const isActive =
                value !== undefined ? child.props.value === value : child.props.active;

              return React.cloneElement(child, {
                active: isActive,
                className: SubframeUtils.twClassNames(
                  child.props.className,
                  {
                    'flex-1': fullWidth,
                    'h-9': size === 'sm',
                    'h-10': size === 'md',
                  },
                  child.props.disabled ? 'cursor-default' : undefined,
                ),
                onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                  if (child.props.onClick) child.props.onClick(e);
                  if (child.props.disabled) return;
                  if (onValueChange) onValueChange(child.props.value);
                },
              });
            })
          : null}
      </div>
    );
  },
);

export const TabsButtonBorder = Object.assign(TabsButtonBorderRoot, { Item });
